import * as RadPopover from '@radix-ui/react-popover'
import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { ArrowDown2Icon, ArrowUp2Icon } from '@root/shared/ui-kit'

import { selectedNetworkAtom, setNetworkAction } from '../../atoms'
import { Network } from '../../model'
import { networks } from '../../model/network'

const NETWORK_LABEL_MAP = {
  [Network.Ethereum]: 'ETH',
  [Network.Solana]: 'SOL',
  [Network.Ton]: 'TON',
  [Network.Base]: 'BASE',
  [Network.Bsc]: 'BSC',
}

const LOGO_SIZE = 24
const ARROW_SIZE = 12

export const NetworkDropdown = () => {
  const [open, setOpen] = useState<boolean>(false)
  const [network] = useAtom(selectedNetworkAtom)
  const setNetwork = useAction(setNetworkAction)
  const filteredNetworks = useMemo(() => {
    return networks.filter(
      (item) =>
        item !== network && item !== Network.Ton && item !== Network.Base,
    )
  }, [network])
  const haptics = useHapticFeedback()

  const handleSelect = (item: Network.Network) => {
    haptics.impactOccurred('light')
    setNetwork(item)
    setOpen(false)
  }
  const handleTriggerClick = () => {
    haptics.impactOccurred('light')
    setOpen(!open)
  }

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger onClick={handleTriggerClick} asChild>
        <Button open={open}>
          <CurrencyNetworkIcon network={network} size={LOGO_SIZE} />
          <Label>{NETWORK_LABEL_MAP[network]}</Label>
          <ArrowDown2Icon size={ARROW_SIZE} />
        </Button>
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="center">
          <Button open={open} onClick={handleTriggerClick}>
            <CurrencyNetworkIcon network={network} size={LOGO_SIZE} />
            <Label>{NETWORK_LABEL_MAP[network]}</Label>
            <ArrowUp2Icon size={ARROW_SIZE} />
          </Button>

          {filteredNetworks.map((item) => (
            <React.Fragment key={item}>
              <Item onClick={() => handleSelect(item)}>
                <CurrencyNetworkIcon network={item} size={LOGO_SIZE} />
                {NETWORK_LABEL_MAP[item]}
              </Item>
            </React.Fragment>
          ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

export const Root = styled(RadPopover.Root)``
export const Trigger = styled(RadPopover.Trigger)``

const Button = styled.button<{ open: boolean }>`
  all: unset;
  background: ${({ open }) =>
    open ? 'var(--background-2)' : 'var(--backgroundA-2)'};
  color: var(--text-12);

  display: grid;
  overflow: hidden;
  grid-template-columns: auto 1fr auto;

  padding-inline-start: 2px;
  border-radius: 50px;
  border: 0;
  gap: 5px;
  align-items: center;
  cursor: pointer;
  position: relative;

  padding-inline-end: 6px;
  height: 28px;
  font-weight: 600;
  font-size: 11px;

  width: 70px;

  transition: all 0.15s ease-in-out;

  &:hover {
    background: var(--backgroundA-3);
  }
`

const Label = styled.span`
  gap: 3px;

  overflow: hidden;
  display: grid;

  text-align: left;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
`

export const Content = styled(RadPopover.Content)`
  background: #020202;
  box-shadow: 2px 2px 9px 1px rgb(0 0 0 / 30%);
  backdrop-filter: blur(22.5px);

  overflow: hidden;
  margin-top: -30px;
  z-index: 2;
  padding: 2px;
  gap: 2px;
  display: grid;

  border-radius: 16px;
  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const Item = styled.div`
  color: var(--text-12);
  background: var(--backgroundA-2);

  border-radius: 30px;
  border: 0;
  transition: all 100ms ease-in;
  width: 100%;

  display: grid;
  align-items: center;
  text-align: left;
  text-transform: uppercase;
  overflow: hidden;
  padding: 0 2px;
  grid-template-columns: auto 1fr;
  gap: 4px;

  font-size: 11px;
  font-weight: 600;
  height: 32px;

  transition: all 0.15s ease-in-out;

  &:hover {
    background: #141414;
    cursor: pointer;
  }
`
